import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PlanProvider, usePlan } from './context/PlanContext';
import { Toaster } from './components/ui/sonner';
import UpgradeRequired from './components/UpgradeRequired';
import { isMarketingSite, APP_URL } from './lib/domains';

// Pages — Public
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import GymRegistrationPage from './pages/GymRegistrationPage';
import PrivacyPage from './pages/PrivacyPage';

// Pages — Protected (Dashboard) — lazy loaded
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const MembersListPage = React.lazy(() => import('./pages/MembersListPage'));
const MemberFormPage = React.lazy(() => import('./pages/MemberFormPage'));
const MemberDetailPage = React.lazy(() => import('./pages/MemberDetailPage'));
const AttendancePage = React.lazy(() => import('./pages/AttendancePage'));
const PaymentsPage = React.lazy(() => import('./pages/PaymentsPage'));
const AnnouncementsPage = React.lazy(() => import('./pages/AnnouncementsPage'));
const MemberDashboard = React.lazy(() => import('./pages/MemberDashboard'));
const MemberClassesPage = React.lazy(() => import('./pages/MemberClassesPage'));
const TrainersPage = React.lazy(() => import('./pages/TrainersPage'));
const TrainerDetailPage = React.lazy(() => import('./pages/TrainerDetailPage'));
const PublicCheckInPage = React.lazy(() => import('./pages/PublicCheckInPage'));
const TrialBookingPage = React.lazy(() => import('./pages/TrialBookingPage'));
const POSPage = React.lazy(() => import('./pages/POSPage'));
const ClassesPage = React.lazy(() => import('./pages/ClassesPage'));
const LeadsPage = React.lazy(() => import('./pages/LeadsPage'));
const TrialsPage = React.lazy(() => import('./pages/TrialsPage'));
const WhatsAppPage = React.lazy(() => import('./pages/WhatsAppPage'));
const PlansPage = React.lazy(() => import('./pages/PlansPage'));
const MemberAppPage = React.lazy(() => import('./pages/MemberAppPage'));

import ReportsPage from './pages/ReportsPage';
import AIChatWidget from './components/AIChatWidget';

import './App.css';

/* ═══════════════════════════════════════════════════════════════════════════
   GLOBAL ERROR BOUNDARY
   ═══════════════════════════════════════════════════════════════════════════ */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    // Production-safe: only log the error message, not the full object/stack
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Uncaught error:', error?.message || 'Unknown error');
    } else {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    }
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error?.message?.match(
        /Loading chunk|Failed to fetch dynamically imported module/i
      );
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0a0a0a] p-6 text-center">
          <div className="max-w-md w-full space-y-4 flex flex-col items-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-500/10 rounded-2xl flex items-center justify-center mb-2">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isChunkError ? 'App Updated!' : 'Something went wrong'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {isChunkError
                ? 'We just released a new version of Fitxero. Please refresh the page to load the latest changes.'
                : 'An unexpected error occurred. Please try refreshing the page.'}
            </p>

            {/* DEBUG INFO FOR CRASH RECOVERY */}
            {!isChunkError && this.state.error && (
              <div className="w-full mt-4 bg-red-50 dark:bg-[#1f0f0f] border border-red-100 dark:border-red-900/30 rounded-lg p-3 text-left overflow-auto max-h-48">
                <p className="text-xs font-mono text-red-600 dark:text-red-400 font-bold mb-1">
                  Crash Details:
                </p>
                <code className="text-[10px] font-mono text-red-500 dark:text-red-300 whitespace-pre-wrap break-all">
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </code>
              </div>
            )}

            <button
              onClick={() => {
                localStorage.removeItem('theme');
                window.location.reload(true);
              }}
              className="mt-6 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold shadow-lg shadow-indigo-500/25 transition-all"
            >
              Hard Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUSPENSE LOADER
   ═══════════════════════════════════════════════════════════════════════════ */
const SuspenseLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0a0a0a]">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-gray-400 font-medium">Loading…</span>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   AUTH GUARD — protects dashboard routes
   ═══════════════════════════════════════════════════════════════════════════ */
const PrivateRoute = ({ children, requireGymOwner = false, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  const { isExpired } = usePlan();

  if (loading) {
    return <SuspenseLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && user.role !== 'superadmin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (requireGymOwner && user.role !== 'gym_owner') {
    return <Navigate to="/login" replace />;
  }

  if (requireGymOwner && isExpired) {
    return <UpgradeRequired />;
  }

  return children;
};

/* ═══════════════════════════════════════════════════════════════════════════
   MARKETING SITE ROUTES (www.fitxero.com)
   Only shows: Landing page, Privacy, and redirects login/register to app domain
   ═══════════════════════════════════════════════════════════════════════════ */
const MarketingRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Redirect login/register to the app domain */}
      <Route path="/login" element={<RedirectToApp path="/login" />} />
      <Route path="/register" element={<RedirectToApp path="/register" />} />

      {/* Any other route → back to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/** Redirect helper: sends users to app.fitxero.com for login/register */
const RedirectToApp = ({ path }) => {
  React.useEffect(() => {
    window.location.href = `${APP_URL}${path}`;
  }, [path]);
  return <SuspenseLoader />;
};

/* ═══════════════════════════════════════════════════════════════════════════
   APP SITE ROUTES (app.fitxero.com)
   Full SaaS: login, register, dashboard, all admin/member pages
   ═══════════════════════════════════════════════════════════════════════════ */
const SmartLanding = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <SuspenseLoader />;
  }

  // Not logged in → show landing page (on app domain too, as a fallback)
  if (!user) {
    return <LandingPage />;
  }

  // Already logged in → go to dashboard
  if (user.role === 'superadmin') {
    return <Navigate to="/superadmin/dashboard" replace />;
  }
  if (user.role === 'gym_owner') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return <Navigate to="/member/dashboard" replace />;
};

const AppRoutes = () => {
  return (
    <React.Suspense fallback={<SuspenseLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<SmartLanding />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<GymRegistrationPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/checkin/:gymId" element={<PublicCheckInPage />} />
        <Route path="/trial/:gymId" element={<TrialBookingPage />} />

        {/* Super Admin */}
        <Route
          path="/superadmin/dashboard"
          element={
            <PrivateRoute requireSuperAdmin>
              <SuperAdminDashboard />
            </PrivateRoute>
          }
        />

        {/* Gym Owner — Admin */}
        <Route
          path="/admin/dashboard"
          element={
            <PrivateRoute requireGymOwner>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members"
          element={
            <PrivateRoute requireGymOwner>
              <MembersListPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/new"
          element={
            <PrivateRoute requireGymOwner>
              <MemberFormPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/:id"
          element={
            <PrivateRoute requireGymOwner>
              <MemberDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/members/:id/edit"
          element={
            <PrivateRoute requireGymOwner>
              <MemberFormPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/attendance"
          element={
            <PrivateRoute requireGymOwner>
              <AttendancePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <PrivateRoute requireGymOwner>
              <PaymentsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pos"
          element={
            <PrivateRoute requireGymOwner>
              <POSPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <PrivateRoute requireGymOwner>
              <AnnouncementsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/trainers"
          element={
            <PrivateRoute requireGymOwner>
              <TrainersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/trainers/:id"
          element={
            <PrivateRoute requireGymOwner>
              <TrainerDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <PrivateRoute requireGymOwner>
              <ReportsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/classes"
          element={
            <PrivateRoute requireGymOwner>
              <ClassesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <PrivateRoute requireGymOwner>
              <LeadsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/trials"
          element={
            <PrivateRoute requireGymOwner>
              <TrialsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/whatsapp"
          element={
            <PrivateRoute requireGymOwner>
              <WhatsAppPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/plans"
          element={
            <PrivateRoute requireGymOwner>
              <PlansPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/member-app"
          element={
            <PrivateRoute requireGymOwner>
              <MemberAppPage />
            </PrivateRoute>
          }
        />

        {/* Member */}
        <Route
          path="/member/dashboard"
          element={
            <PrivateRoute>
              <MemberDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/member/classes"
          element={
            <PrivateRoute>
              <MemberClassesPage />
            </PrivateRoute>
          }
        />

        {/* Catchall → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ROOT — Picks the right route set based on domain
   ═══════════════════════════════════════════════════════════════════════════ */
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PlanProvider>
          <ErrorBoundary>
            <BrowserRouter>
              {isMarketingSite ? <MarketingRoutes /> : <AppRoutes />}
              {/* Only show AI chat on the app domain */}
              {!isMarketingSite && <AIChatWidget />}
              <Toaster position="top-right" />
            </BrowserRouter>
          </ErrorBoundary>
        </PlanProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
